import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea } from "@supportinitiative/react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { inlineQueryOperators, operators } from "../../../UseCaseUtils";
import { getId } from "../../../../utilities";
import { useLoaders } from "src/hooks";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import {
  UAction,
  UColumnsDeriveDataActionType,
  UCustomDeriveDataActionType,
  UDeriveDataActionType,
  URowsDeriveDataActionType,
  UseCaseType,
} from "@supportinitiative/common";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

function getDatasetsLOV(recon?: UseCaseType["definition"]["recons"][number]) {
  if (recon) {
    if (recon.definition.datasets) {
      const datasets = recon.definition.datasets.map((d) => {
        return {
          value: d.id,
          label: `${d.name} [ ${d.id} ]`,
        };
      });
      return [...datasets, { value: "__ALL", label: "__ALL" }];
    } else {
      return [{ value: "__ALL", label: "__ALL" }];
    }
  } else {
    return [];
  }
}

const UReconDeriveDataColumnsSchema = object({
  dataPopulation: array()
    .of(
      object({
        column: string().required(),
        genFunction: string().required(),
        id: string().nullable(),
      }),
    )
    .min(1, "At least one column is required"),
});

const UReconDeriveDataRowsSchema = object({
  dataPopulation: object({
    genFunction: string().required(),
    id: string().nullable(),
  }),
});

const UReconDeriveDataCustomSchema = object({
  dataPopulation: object({
    genFunction: string().required(),
  }),
});

const UDeriveDataActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["DERIVE_DATA"]),
  description: string().nullable(),
  definition: object({
    dataset: string().required(),
    type: string().required().oneOf(["COLUMNS", "ROWS", "CUSTOM"]),
  }),
});

function Rows({
  name,
  control,
  disabled,
}: {
  name: "";
  control: Control<UAction<URowsDeriveDataActionType>, any>;
  disabled: boolean;
}) {
  let columns: { label: string; value: string }[] = [];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Divider my="xs" label="Derivation" labelPosition="center" />

      <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
        {/*  <Grid.Col span={12}>
          <CMultiSelect
            name={`rows.columns`}
            control={control}
            label="Columns"
            required
            data={columns}
            searchable
            showErrorText={false}
            disabled={disabled || false}
          />
        </Grid.Col> */}

        <Grid.Col span={12}>
          <CTextarea
            name={`definition.config.dataPopulation.genFunction`}
            control={control}
            label="Gen Function"
            required
            showErrorText={false}
            disabled={disabled || false}
            minRows={4}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
}

function Custom({
  name,
  control,
  disabled,
}: {
  name: "";
  control: Control<UAction<UCustomDeriveDataActionType>, any>;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Divider my="xs" label="Derivation" labelPosition="center" />

      <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
        <Grid.Col span={12}>
          <CTextarea
            name={`definition.config.dataPopulation.genFunction`}
            control={control}
            label="Gen Function"
            required
            showErrorText={false}
            minRows={4}
            disabled={disabled || false}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
}

function Columns({
  name,
  control,
  disabled,
}: {
  name: "";
  control: Control<UAction<UColumnsDeriveDataActionType>>;
  disabled: boolean;
}) {
  const dataPopulation = useFieldArray({
    control,
    name: `definition.config.dataPopulation`,
  });

  return (
    <Stack gap="0">
      <Divider my="xs" label="Derivation" labelPosition="center" />
      <Flex>
        <Group mb="xs" style={{ alignItems: "center" }}>
          <Text fw={500} style={{ fontSize: "14px" }}>
            Columns
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              dataPopulation.append({
                column: "",
                genFunction: "",
                id: getId(),
              });
            }}
            disabled={disabled || false}
          >
            <IconPlus />
          </ActionIcon>
        </Group>
      </Flex>
      <Stack>
        {dataPopulation.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
              <Grid.Col span={3}>
                <CTextInput
                  name={`definition.config.dataPopulation.${index}.column`}
                  control={control}
                  label={"Column"}
                  disabled={disabled || false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <CTextarea
                  name={`definition.config.dataPopulation.${index}.genFunction`}
                  control={control}
                  label={"Generate Function"}
                  minRows={4}
                  disabled={disabled || false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <CTextInput
                  name={`definition.config.dataPopulation.${index}.id`}
                  control={control}
                  label={"Id"}
                  disabled={true}
                />
              </Grid.Col>
              <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    dataPopulation.remove(index);
                  }}
                  disabled={disabled || false}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </Stack>
    </Stack>
  );
}

function DeriveData({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UDeriveDataActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        type: action.definition.type || "CUSTOM",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(UDeriveDataActionSchema),
  });

  const type = useWatch({
    control,
    name: `definition.type`,
  });

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Derive Data</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Dataset" />
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CSelect
              name={`definition.dataset`}
              control={control}
              label={"Dataset"}
              data={getDatasetsLOV(recon)}
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "CUSTOM",
                  label: "Custom",
                },
                /* 
                {
                  value: "COLUMNS",
                  label: "Columns",
                },
                 {
                  value: "ROWS",
                  label: "Rows",
                },
                {
                  value: "CUSTOM",
                  label: "Custom",
                }, */
              ]}
              showErrorText={false}
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        {type === "CUSTOM" ? (
          <Custom
            name={""}
            control={control as Control<UAction<UCustomDeriveDataActionType>, any>}
            disabled={!!useCaseFormState.actionOptions.disableAll}
          />
        ) : type === "ROWS" ? (
          <Rows
            name={""}
            control={control as Control<UAction<URowsDeriveDataActionType>, any>}
            disabled={!!useCaseFormState.actionOptions.disableAll}
          />
        ) : type === "COLUMNS" ? (
          <Columns
            name={""}
            control={control as Control<UAction<UColumnsDeriveDataActionType>, any>}
            disabled={!!useCaseFormState.actionOptions.disableAll}
          />
        ) : (
          <></>
        )}
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await UDeriveDataActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        if (values.definition.type === "COLUMNS") {
                          await UReconDeriveDataColumnsSchema.validate(values.definition.config, {
                            abortEarly: false,
                          });
                        } else if (values.definition.type === "ROWS") {
                          await UReconDeriveDataRowsSchema.validate(values.definition.config, {
                            abortEarly: false,
                          });
                        } else if (values.definition.type === "CUSTOM") {
                          await UReconDeriveDataCustomSchema.validate(values.definition.config, {
                            abortEarly: false,
                          });
                        }

                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default DeriveData;
